import React from 'react'
import moment from 'moment'
import {Badge} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {NodeContact} from '../../../../types/contactsApi'
import {PhoneProviderActions} from '../../../../components/telephony/PhoneProviderActions'
import {UserAvatar} from '../../../../components/UserAvatar'
import {AgentInfoTooltip} from '../../../shared/AgentInfoTooltip'
import {parseId} from '../../../../helpers'

const sp = ' '

const initialsOf = (contact: NodeContact) => {
  const first = contact.firstName?.trim()?.[0] || contact.fullName?.trim()?.[0] || '?'
  const last = contact.lastName?.trim()?.[0] || ''
  return `${first}${last}`
}

interface ContactSummarySideProps {
  contact: NodeContact
  policiesCount: number
}

/** Tarjeta lateral del perfil del cliente, equivalente a `PolicySummary` en OnePolicyPage. */
export const ContactSummarySide: React.FC<ContactSummarySideProps> = ({contact, policiesCount}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const age = contact.dateBirth ? moment().diff(moment(contact.dateBirth), 'years') : null
  const tags = contact.tagAssignments ?? []

  return (
    <div>
      <div className='d-flex align-items-center gap-3 my-3'>
        <span className='initial-names-rounded' style={{cursor: 'default'}}>
          {initialsOf(contact)}
        </span>
        <div className='flex-grow-1' style={{minWidth: 0}}>
          <div className='fw-bold text-dark fs-5 d-flex align-items-center gap-2'>
            <span className='text-truncate'>{contact.fullName || na}</span>
            {contact.isImportant && (
              <i
                className='bi bi-star-fill'
                style={{color: 'goldenrod'}}
                title={intl.formatMessage({
                  id: 'contacts.profile.summary.important',
                  defaultMessage: 'Important contact',
                })}
              />
            )}
          </div>
          <div className='text-muted' style={{fontSize: '0.9rem'}}>
            <FormattedMessage id='contacts.profile.summary.contactId' defaultMessage='ID:' />
            {sp}
            {parseId(contact.id)}
          </div>
        </div>
      </div>

      {tags.length > 0 && (
        <div className='d-flex flex-wrap gap-1 mb-3'>
          {tags.map(({tag}) => (
            <Badge key={tag.id} style={{backgroundColor: tag.color}} className='text-white'>
              {tag.name}
            </Badge>
          ))}
        </div>
      )}

      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='contacts.profile.summary.phone' defaultMessage='Phone:' />
        </span>
        <span className='kt-widget__data'>
          <PhoneProviderActions phone={contact.phone} emptyText={na} />
        </span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='contacts.profile.summary.email' defaultMessage='Email:' />
        </span>
        <span className='kt-widget__data text-break'>{contact.email || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage
            id='contacts.profile.summary.dateBirth'
            defaultMessage='Date of Birth:'
          />
        </span>
        <span className='kt-widget__data'>
          {contact.dateBirth ? moment(contact.dateBirth).format('MM/DD/YYYY') : na}
          {age !== null && (
            <span className='text-muted'>
              {sp}
              <FormattedMessage
                id='contacts.profile.summary.years'
                defaultMessage='({age, plural, one {# yr} other {# yrs}})'
                values={{age}}
              />
            </span>
          )}
        </span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='contacts.profile.summary.location' defaultMessage='Location:' />
        </span>
        <span className='kt-widget__data'>
          {[contact.city, contact.stateEU?.code || contact.stateEU?.name, contact.zipCode]
            .filter(Boolean)
            .join(', ') || na}
        </span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='contacts.profile.summary.policies' defaultMessage='Policies:' />
        </span>
        <span className='kt-widget__data'>{policiesCount}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage
            id='contacts.profile.summary.agentOwner'
            defaultMessage='Agent (Owner):'
          />
        </span>
        <span className='kt-widget__data d-inline-flex align-items-center gap-2'>
          {contact.user ? (
            <>
              <UserAvatar user={contact.user} tooltipId={`contact-owner-${contact.id}`} size={28} />
              <AgentInfoTooltip id={`contact-owner-${contact.id}`} user={contact.user} />
              <span>
                {contact.user.firstName} {contact.user.lastName}
              </span>
            </>
          ) : (
            na
          )}
        </span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='contacts.profile.summary.created' defaultMessage='Created:' />
        </span>
        <span className='kt-widget__data'>
          {contact.created ? moment(contact.created).format('MMM DD, YYYY') : na}
        </span>
      </div>
    </div>
  )
}
